import React, { useEffect, useRef, useState } from 'react';

const names = { 1: 'Health', 2: 'Combat', 3: 'Physical', 4: 'Stealth', 5: 'Perception', 6: 'Magic', 7: 'Meditation', 8: 'Academic', 9: 'Social' };

export function SkillProgressDetails({ progression, secondarySkills = [] }) {
  if (!progression) return null;
  return <details className="play-skill-progress">
    <summary>Skill progress — {progression.credits}/9 primary improvements</summary>
    <ul>{Object.entries(progression.primary).map(([id, skill]) => <li key={id}>{names[id]} {skill.rank}: {skill.attempts}/{skill.threshold} attempts; {skill.gains}/2 ranks this level</li>)}</ul>
    <ul>{Object.entries(progression.secondary).map(([id, skill]) => <li key={id}>{secondarySkills.find(row => Number(row.idSecondarySkillList) === Number(id))?.name || `Skill ${id}`} {skill.rank}: {skill.attempts}/{skill.threshold} attempts{skill.overflow ? `; next gain improves ${names[skill.primaryId]}` : ''}</li>)}</ul>
  </details>;
}

export default function SkillProgressionPanel({ progression, onAllocate }) {
  const [points, setPoints] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(null);
  const dialog = useRef(null);
  const pending = progression?.phase === 'allocationRequired';
  const transitionId = progression?.transition?.id;
  useEffect(() => { setPoints({}); setError(''); request.current = null; }, [transitionId]);
  useEffect(() => { if (pending) dialog.current?.focus(); }, [pending, transitionId]);
  if (!progression) return null;
  const total = Object.values(points).reduce((sum, value) => sum + value, 0);
  const submit = async () => {
    setBusy(true); setError('');
    request.current ||= { idempotencyKey: window.crypto?.randomUUID?.() || `${transitionId}:${Date.now()}`,
      transitionId, revision: progression.revision,
      allocations: Object.entries(points).filter(([, value]) => value > 0).map(([id, value]) => ({ primarySkillId: Number(id), points: value })) };
    try { await onAllocate(request.current); } catch (failure) { setError(failure.message || 'Could not allocate points.'); }
    finally { setBusy(false); }
  };
  return <>
    {pending && <div className="play-character-sheet-overlay play-progression-overlay" role="dialog" aria-modal="true" aria-label="Allocate earned skill points"
      ref={dialog} tabIndex={-1} onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); }
        if (event.key !== 'Tab') return;
        const controls = [...dialog.current.querySelectorAll('input:not(:disabled), button:not(:disabled)')];
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }}>
      <div className="play-character-sheet-modal play-progression-dialog">
        <h2>Level {progression.transition.nextLevel} earned</h2>
        <p>Allocate all {progression.kitty} primary skill points to begin the next level. {progression.kitty - total} remain.</p>
        {Object.entries(progression.transition.room).map(([id, room]) => <label key={id} style={{ display: 'block' }}>{names[id]} ({room} available)
          <input aria-label={`${names[id]} points`} type="number" min="0" max={room} step="1" value={points[id] || 0} disabled={busy || !room}
            onChange={event => { const value = Number(event.target.value); if (Number.isInteger(value) && value >= 0 && value <= room) { setPoints(current => ({ ...current, [id]: value })); request.current = null; } }} />
        </label>)}
        {error && <p role="alert">{error}</p>}
        <button type="button" disabled={busy || total !== progression.kitty} onClick={submit}>{busy ? 'Saving…' : 'Begin next level'}</button>
      </div>
    </div>}
  </>;
}
